import * as React from 'react'
import { Dialog as BaseDialog } from '@base-ui/react/dialog'

import { DialogTrigger } from './nested/trigger'
import { DialogPortal } from './nested/portal'
import { DialogBackdrop } from './nested/backdrop'
import { DialogPopup } from './nested/popup'
import { DialogContent } from './nested/content'
import { DialogHeader } from './nested/header'
import { DialogFooter } from './nested/footer'
import { DialogBody } from './nested/body'
import { DialogTitle } from './nested/title'
import { DialogDescription } from './nested/description'
import { DialogClose } from './nested/close'
import { DialogIcon } from './nested/icon'
import type { PartProps } from '../../lib/part-props'

type DialogRootProps = PartProps<React.ComponentProps<typeof BaseDialog.Root>>
type ChangeEventDetails = Parameters<NonNullable<DialogRootProps['onOpenChange']>>[1]

export interface DialogProps extends DialogRootProps {
  /** When true, only allow closing via explicit close button clicks (not Escape or backdrop) */
  disableEscape?: boolean
}

function Dialog({ children, disableEscape, onOpenChange, ...props }: DialogProps) {
  const handleOpenChange = React.useCallback(
    (isOpen: boolean, eventDetails: ChangeEventDetails) => {
      if (disableEscape) {
        if (!isOpen && !(eventDetails as { trigger?: unknown }).trigger) {
          ;(eventDetails as { cancel?: () => void }).cancel?.()
          return
        }
      }
      onOpenChange?.(isOpen, eventDetails)
    },
    [disableEscape, onOpenChange],
  )

  return (
    <BaseDialog.Root {...props} onOpenChange={handleOpenChange}>
      {children}
    </BaseDialog.Root>
  )
}

Dialog.Trigger = DialogTrigger
Dialog.Portal = DialogPortal
Dialog.Backdrop = DialogBackdrop
Dialog.Popup = DialogPopup
Dialog.Title = DialogTitle
Dialog.Description = DialogDescription
Dialog.Close = DialogClose
Dialog.Content = DialogContent
Dialog.Header = DialogHeader
Dialog.Body = DialogBody
Dialog.Footer = DialogFooter
Dialog.Icon = DialogIcon

export { Dialog }
